import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import {
  apiGetQuestionnairesStatsQuestionnaireTypePatientPatientId,
  PerPatientQuestionStats,
  QuestionnaireTypes,
} from "../../../api";
import { Spinner } from "../../../components/ui-kit/spinner";
import {
  type Column,
  DataTable,
} from "../../../components/ui-kit/tables/data-table";
import { questionnaireTypes } from "../../../constants/enum-to-text";
import { Patient } from "../patient";
import { QuestionnaireStatsModel } from "./questionnaire-stats-model";

export const QuestionnaireStats = observer(function QuestionnaireStats(props: {
  type: QuestionnaireTypes;
  patient: Patient;
}) {
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState<QuestionnaireStatsModel>();
  const userUUID = props.patient.api.requireUserUUID();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res =
          await apiGetQuestionnairesStatsQuestionnaireTypePatientPatientId({
            path: {
              questionnaire_type: props.type,
              patient_id: props.patient.patientId,
            },
          });

        const m = new QuestionnaireStatsModel(
          res.data.responses_stats,
          props.patient.api.permissions,
          userUUID,
        );

        m.init();

        setModel(m);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [
    props.type,
    props.patient.patientId,
    props.patient.api.permissions,
    userUUID,
  ]);

  const columns = useMemo((): Column<PerPatientQuestionStats>[] => {
    if (!model) {
      return [];
    }

    const dates = new Set<string>();

    for (const questionStats of model.data) {
      for (const date of Object.keys(questionStats.per_submission)) {
        dates.add(date);
      }
    }

    const dateColumns: Column<PerPatientQuestionStats>[] = [...dates.values()]
      .sort()
      .map((date) => ({
        header: date,
        cell: ({ item }) => item.per_submission[date],
      }));

    return [
      { header: <Trans>Question</Trans>, cell: ({ item }) => item.question },
      ...dateColumns,
      { header: <Trans>Summary</Trans>, cell: ({ item }) => item.summary },
    ];
  }, [model]);

  return (
    <div>
      <h4 className="mt-5 mb-5 font-bold">
        <Trans>Stats for {questionnaireTypes[props.type]}:</Trans>
      </h4>

      {loading ? (
        <Spinner size={20} />
      ) : model && model.data.length > 0 ? (
        <DataTable
          columns={columns}
          model={model}
          itemKeyField="question"
          rowsPerPage={Number.MAX_VALUE}
        />
      ) : (
        <Trans>No responses yet</Trans>
      )}
    </div>
  );
});
